// The packaging cell's expander: a plain in-flow <Tr colSpan> rendered right
// below the row it belongs to (no portal, no overlay) — ported from the
// item-board sentence editor (inline-variant-form.tsx's bareSelect/bareInput
// "Packaging" block), rebound to the grid row's onChange patches instead of
// local useState.
//
// The editor's inner fields live in PackagingEditorFields so the mobile
// variant card (variant-card.tsx) can render the exact same sentence editor
// inside a Box — the Tr/Td wrapper here is desktop-table-only.
import { useGetMasterProductUsageApi } from 'apis/master-products';
import { Box, ChakraSelect, Flex, Input, Td, Text, Tr } from 'design-system';
import { UOM_HELP } from 'pages/inquiry-processor/components/master-product-families/uom-field-meta';
import { buildCanonicalOptions } from 'pages/inquiry-processor/utils/canonical-options';
import { plural, round } from 'pages/inquiry-processor/utils/describe-units';
import type { PackagingFields } from '../packaging';

interface FieldsProps {
  fields: PackagingFields;
  uomOptions: readonly string[];
  onChange: (patch: Partial<PackagingFields>) => void;
  // Persisted variant id — undefined for unsaved rows, which can have no
  // recorded prices and fetch nothing.
  variantId?: number;
  // True when the ladder fields differ from their saved state (see
  // useEditableVariantsRows.packagingDirtyClientIds).
  isPackagingDirty?: boolean;
}

interface Props extends FieldsProps {
  colSpan: number;
}

export const PackagingEditorFields = ({ fields, uomOptions, onChange, variantId, isPackagingDirty }: FieldsProps) => {
  // Fetched when the expander opens (this component only mounts expanded), so
  // the recorded-price count is already on hand the moment a ladder field
  // turns dirty. `deals` = live supplier_prices rows for this variant.
  const { data: usage } = useGetMasterProductUsageApi(variantId, { enabled: variantId != null });
  const recordedPrices = usage?.deals ?? 0;
  const { baseUnit, itemUnit, netContent, packUnit, packSize } = fields;
  const packSizeNum = packSize.trim() ? Number(packSize) : null;
  const netContentNum = netContent.trim() ? Number(netContent) : null;
  const packageTotal =
    netContentNum != null && packSizeNum != null && baseUnit && packUnit
      ? `${round(netContentNum * packSizeNum)} ${baseUnit} per ${packUnit}`
      : null;

  const bareSelect = (
    canon: readonly string[],
    value: string,
    set: (v: string) => void,
    placeholder: string,
    labelFor?: (v: string) => string
  ) => {
    const options = buildCanonicalOptions(canon, value, labelFor);
    const selected = options.find((o) => o.value === value) ?? null;
    return (
      <ChakraSelect
        size='sm'
        options={options}
        value={selected}
        onChange={(opt: any) => set(opt?.value ?? '')}
        placeholder={placeholder}
        isClearable
        menuPortalTarget={document.body}
        menuPosition='fixed'
        styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
      />
    );
  };

  const bareInput = (value: string, set: (v: string) => void, numeric: 'decimal' | 'integer') => (
    <Input
      size='sm'
      bg='white'
      type='number'
      min={0}
      step={numeric === 'integer' ? 1 : 'any'}
      value={value}
      onChange={(e) => set(e.target.value)}
    />
  );

  return (
    <>
      <Flex wrap='wrap' align='center' gap='2' fontSize='sm' py='1'>
        <Text>1</Text>
        <Box w={{ base: '45%', md: '130px' }}>
          {bareSelect(uomOptions, packUnit, (v) => onChange({ packUnit: v }), 'e.g. case')}
        </Box>
        <Text>contains</Text>
        <Box w={{ base: '26%', md: '70px' }}>{bareInput(packSize, (v) => onChange({ packSize: v }), 'integer')}</Box>
        <Box w={{ base: '45%', md: '130px' }}>
          {bareSelect(
            uomOptions,
            itemUnit,
            (v) => onChange({ itemUnit: v }),
            'e.g. piece',
            (v) => plural(v, packSizeNum ?? 2)
          )}
        </Box>
        <Text>of</Text>
        <Box w={{ base: '26%', md: '70px' }}>
          {bareInput(netContent, (v) => onChange({ netContent: v }), 'decimal')}
        </Box>
        <Flex align='center' gap='0.5'>
          <Box w={{ base: '45%', md: '110px' }} minW='110px'>
            {bareSelect(uomOptions, baseUnit, (v) => onChange({ baseUnit: v }), 'e.g. kg')}
          </Box>
          <Text color='red.500'>*</Text>
        </Flex>
        <Text>each{packageTotal ? '' : '.'}</Text>
        {packageTotal && (
          <>
            <Text>—</Text>
            <Text fontWeight='600'>{packageTotal}.</Text>
          </>
        )}
      </Flex>
      <Text fontSize='xs' color='gray.500' mt='1'>
        <Text as='span' color='red.500'>
          *
        </Text>{' '}
        {UOM_HELP.baseUnit.label} is required — it&apos;s what lets us compare prices across variants. The rest describe
        how it&apos;s packaged and are optional.
      </Text>
      {/* Only for a real ladder change on a variant that carries recorded
          prices: the operator fixing packaging is usually not the one who
          logged them. Info, not a gate — prices stay as entered per their
          rung; only the derived per-base-unit figures move on save. */}
      {isPackagingDirty && recordedPrices > 0 && (
        <Text fontSize='xs' color='orange.600' fontWeight='medium' mt='1'>
          {recordedPrices} {plural('supplier price', recordedPrices)} {recordedPrices === 1 ? 'is' : 'are'} recorded
          against this packaging — prices stay as entered per their unit; per-{fields.baseUnit || 'base-unit'} figures
          re-derive on save.
        </Text>
      )}
    </>
  );
};

export const PackagingEditorRow = ({ colSpan, ...fieldsProps }: Props) => {
  return (
    <Tr>
      <Td colSpan={colSpan} bg='gray.50' borderTopWidth='0'>
        <PackagingEditorFields {...fieldsProps} />
      </Td>
    </Tr>
  );
};
